/**
 * Daily Rewards API client
 *
 * Three typed calls to the LUDOKA proxy endpoints defined in
 * docs/architecture/features/daily-matchmaking-rewards/index.md §6.
 *
 * Calls go through the shared api-client which attaches the user JWT
 * (Bearer token) automatically via the auth interceptor.
 */

import { get, post } from '@/lib/api-client';
import type { ClaimResponse, MyDayResponse, PodiumResponse } from '../types';

export interface GetPodiumParams {
  /** Closed day to query (YYYY-MM-DD, GMT+3). Defaults to the most recent closed day. */
  day?: string;
  /** Maximum number of podium entries to return. Defaults to server-configured palier. */
  limit?: number;
}

function buildQueryString(params: GetPodiumParams): string {
  const sp = new URLSearchParams();
  if (params.day) sp.set('day', params.day);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export const dailyRewardsApi = {
  /**
   * GET /api/daily-rewards/me
   * Returns personal daily standing (rank, valid matches, claimable bonus).
   */
  getMyDay: (): Promise<MyDayResponse> => get<MyDayResponse>('/daily-rewards/me'),

  /**
   * GET /api/daily-rewards/podium?day=&limit=
   * Returns the podium for a closed day with browsable history.
   */
  getPodium: (params: GetPodiumParams = {}): Promise<PodiumResponse> =>
    get<PodiumResponse>(`/daily-rewards/podium${buildQueryString(params)}`),

  /**
   * POST /api/daily-rewards/claim { day }
   * Claims a pending bonus for the given day. Idempotent.
   *
   * Error codes (surfaced by SOKA via the LUDOKA proxy):
   *   404 NOTHING_TO_CLAIM
   *   410 BONUS_EXPIRED
   *   409 ALREADY_CLAIMED
   *   409 REWARDS_DISABLED
   */
  claim: (day: string): Promise<ClaimResponse> =>
    post<ClaimResponse>('/daily-rewards/claim', { day })
};
